import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  useRevertRunMutation,
  useSystemBusy,
  useSystemRunsQuery,
} from '../../../hooks/useSystemQueries';
import { SystemRun, SystemRunKind, SystemRunUser } from '../../../types/system';
import { parseApiError } from '../../../utils/parseApiError';
import { RunDetailsDialog } from './RunDetailsDialog';

const formatRunUser = (u?: SystemRunUser | null) =>
  u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.phone || u._id : '—';

const formatSummary = (s?: Record<string, unknown>) =>
  s
    ? Object.entries(s)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join(', ')
    : '';

const canRevert = (r: SystemRun) =>
  r.kind === 'migration' && (r.status === 'succeeded' || r.status === 'failed');

/** History of one kind of run; migrations also get Details and Revert. */
export const RunHistory = ({ kind }: { kind: SystemRunKind }) => {
  const { t } = useTranslation();
  const runs = useSystemRunsQuery(kind);
  const revert = useRevertRunMutation();
  const busy = useSystemBusy();
  const [details, setDetails] = useState<string | null>(null);
  const [reverting, setReverting] = useState<SystemRun | null>(null);

  const confirmRevert = async () => {
    const run = reverting;
    setReverting(null);
    if (!run) return;
    try {
      await revert.mutateAsync(run._id);
    } catch {
      // Shown from revert.error.
    }
  };

  const list = runs.data ?? [];
  return (
    <section className="team-panel">
      <header className="team-panel-header">
        <h3 className="team-panel-title">{t('dashboard.system.history.title')}</h3>
      </header>
      {revert.error && (
        <p className="role-form-error system-panel-body" role="alert">
          {parseApiError(revert.error).message}
        </p>
      )}
      {list.length === 0 ? (
        <p className="team-muted team-panel-empty">{t('dashboard.system.history.empty')}</p>
      ) : (
        <ul className="system-list">
          {list.map((r) => (
            <li key={r._id} className="system-row">
              <div className="system-row-main">
                <div className="system-row-title">
                  {r.name}
                  <span className={`system-status system-status-${r.status}`}>
                    {t(`dashboard.system.status.${r.status}`)}
                  </span>
                </div>
                <div className="team-muted">
                  {[
                    new Date(r.startedAt).toLocaleString(),
                    formatRunUser(r.startedBy),
                    formatSummary(r.summary),
                    r.error,
                  ]
                    .filter(Boolean)
                    .join(' — ')}
                </div>
                {r.status === 'reverted' && (
                  <div className="team-muted">
                    {t('dashboard.system.history.revertedBy', {
                      who: formatRunUser(r.revertedBy),
                      when: r.revertedAt ? new Date(r.revertedAt).toLocaleString() : '',
                    })}{' '}
                    {formatSummary(r.revertSummary)}
                  </div>
                )}
              </div>
              <div className="system-actions">
                {r.kind === 'migration' && (
                  <button type="button" className="secondary-action-btn" onClick={() => setDetails(r._id)}>
                    {t('dashboard.system.history.details')}
                  </button>
                )}
                {canRevert(r) && (
                  <button
                    type="button"
                    className="danger-action-btn"
                    disabled={busy}
                    onClick={() => setReverting(r)}
                  >
                    {t('dashboard.system.history.revert')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <RunDetailsDialog runId={details} onClose={() => setDetails(null)} />
      <Dialog open={Boolean(reverting)} onClose={() => setReverting(null)}>
        <DialogTitle>{t('dashboard.system.history.revertTitle')}</DialogTitle>
        <DialogContent>{t('dashboard.system.history.revertBody')}</DialogContent>
        <DialogActions>
          <button type="button" className="secondary-action-btn" onClick={() => setReverting(null)}>
            {t('common.cancel')}
          </button>
          <button type="button" className="danger-action-btn" disabled={busy} onClick={() => void confirmRevert()}>
            {t('dashboard.system.history.revert')}
          </button>
        </DialogActions>
      </Dialog>
    </section>
  );
};
